import {
  AutomationType,
  TriggerType,
  TriggerTypeClient,
  AutomationIfElseFirstColumnType,
  AutomationIfElseFirstColumnTypeClient,
} from 'helpers/enum';
import {
  ACTIVITY_LABEL_MAP,
  makeActivityConditionItem,
  liveFirst,
} from './helpers';

function itemsForColumnIds(conditionOptions, columnIds) {
  return conditionOptions
    .filter(c => (c.customFieldIfElseId !== null && c.customFieldIfElseId !== undefined) && columnIds.includes(c.ifElseFirstColumnId))
    .map(c => ({ label: c.ifElseFirstColumnName, status: 'live', data: c }));
}

export function buildConditionGroups(conditionOptions, automationType, triggerType) {
  const isClient = automationType === AutomationType.Client;

  const activityCondition =
    conditionOptions.find(c => c.ifElseFirstColumnId === 1) || conditionOptions[0];

  const engagementActivityIds = [6, 5, 17, 4, 23, 15, 7, 8, 24, 16, 26];
  const engagementItems = engagementActivityIds.map(id => ({
    label: ACTIVITY_LABEL_MAP[id],
    status: 'live',
    data: makeActivityConditionItem(activityCondition, id, ACTIVITY_LABEL_MAP[id]),
  }));
  const roiScoreCondition = conditionOptions.find(
    c => c.ifElseFirstColumnName && c.ifElseFirstColumnName.toLowerCase() === 'roi score'
  );
  engagementItems.push({ label: 'Roi score', status: roiScoreCondition ? 'live' : 'soon', data: roiScoreCondition });

  const websiteActivityIds = [22, 20, 21];
  const websiteItems = websiteActivityIds.map(id => ({
    label: ACTIVITY_LABEL_MAP[id],
    status: 'live',
    data: makeActivityConditionItem(activityCondition, id, ACTIVITY_LABEL_MAP[id]),
  }));
  const clientWebsiteItems = [
    { label: 'Page view', status: 'live', data: makeActivityConditionItem(activityCondition, 22, 'Page view') },
    { label: 'Custom view', status: 'live', data: makeActivityConditionItem(activityCondition, 25, 'Custom view') },
  ];

  function findCond(name) {
    return conditionOptions.find(
      c => c.ifElseFirstColumnName && c.ifElseFirstColumnName.toLowerCase() === name.toLowerCase()
    ) || null;
  }

  function findCondById(id) {
    return conditionOptions.find(c => c.ifElseFirstColumnId === id) || null;
  }

  const surveyScoreCond = findCond('survey score');
  const formScoreCond = findCond('form score');
  const npsCond = findCond('net promoter score');
  const surveysFormsItems = [
    { label: 'Survey Opened', status: 'live', data: makeActivityConditionItem(activityCondition, 18, 'Survey Opened') },
    { label: 'Survey completed', status: 'live', data: makeActivityConditionItem(activityCondition, 19, 'Survey completed') },
    { label: 'Form completed', status: 'live', data: makeActivityConditionItem(activityCondition, 29, 'Form completed') },
    { label: 'Survey score', status: surveyScoreCond ? 'live' : 'soon', data: surveyScoreCond },
    { label: 'Form score', status: formScoreCond ? 'live' : 'soon', data: formScoreCond },
    { label: 'Net promoter score', status: npsCond ? 'live' : 'soon', data: npsCond },
  ];

  const activitiesItems = [{ label: 'Activity logged', status: 'soon', data: null }];

  const customFieldColumnIds = isClient
    ? [AutomationIfElseFirstColumnTypeClient.ClientCustomFieldList, AutomationIfElseFirstColumnTypeClient.ClientCustomFieldMultiSelectList, AutomationIfElseFirstColumnTypeClient.ClientCustomFieldDate]
    : [AutomationIfElseFirstColumnType.CandidateCustomFieldList, AutomationIfElseFirstColumnType.CandidateCustomFieldMultiSelectList, AutomationIfElseFirstColumnType.CandidateCustomFieldDate];
  const placementCustomFieldColumnIds = isClient
    ? [
        AutomationIfElseFirstColumnTypeClient.ClientPlacementCustomFieldList,
        AutomationIfElseFirstColumnTypeClient.ClientPlacementCustomFieldMultiSelectList,
        AutomationIfElseFirstColumnTypeClient.ClientPlacementCustomFieldDate,
      ]
    : [
        AutomationIfElseFirstColumnType.PlacementCustomFieldList,
        AutomationIfElseFirstColumnType.PlacementCustomFieldMultiSelectList,
        AutomationIfElseFirstColumnType.PlacementCustomFieldDate,
      ];
  const jobCustomFieldColumnIds = isClient
    ? [
        AutomationIfElseFirstColumnTypeClient.ClientJobCustomFieldList,
        AutomationIfElseFirstColumnTypeClient.ClientJobCustomFieldMultiSelectList,
        AutomationIfElseFirstColumnTypeClient.ClientJobCustomFieldDate,
      ]
    : [
        AutomationIfElseFirstColumnType.JobCustomFieldList,
        AutomationIfElseFirstColumnType.JobCustomFieldMultiSelectList,
        AutomationIfElseFirstColumnType.JobCustomFieldDate,
      ];
  // Application custom fields are candidate-side only, and get their own separate section (not merged into
  // Job Custom Fields).
  const applicationCustomFieldColumnIds = [
    AutomationIfElseFirstColumnType.ApplicationCustomFieldList,
    AutomationIfElseFirstColumnType.ApplicationCustomFieldMultiSelectList,
    AutomationIfElseFirstColumnType.ApplicationCustomFieldDate,
  ];

  const customFieldItems = itemsForColumnIds(conditionOptions, customFieldColumnIds);
  const placementCustomFieldItems = itemsForColumnIds(conditionOptions, placementCustomFieldColumnIds);
  const jobCustomFieldItems = itemsForColumnIds(conditionOptions, jobCustomFieldColumnIds);
  const applicationCustomFieldItems = isClient ? [] : itemsForColumnIds(conditionOptions, applicationCustomFieldColumnIds);

  if (isClient) {
    return buildClientConditionGroups(triggerType, {
      conditionOptions, findCond, findCondById,
      engagementItems, clientWebsiteItems, surveysFormsItems, activitiesItems, customFieldItems, placementCustomFieldItems, jobCustomFieldItems,
    });
  }

  return buildCandidateConditionGroups(triggerType, {
    findCond, findCondById,
    engagementItems, websiteItems, surveysFormsItems, activitiesItems, customFieldItems, placementCustomFieldItems, jobCustomFieldItems, applicationCustomFieldItems,
  });
}

function buildClientConditionGroups(triggerType, ctx) {
  const { findCond, findCondById, engagementItems, clientWebsiteItems, surveysFormsItems, activitiesItems, customFieldItems, placementCustomFieldItems, jobCustomFieldItems } = ctx;

  const contactFieldDefs = [
    { label: 'Status', name: 'status' },
    { label: 'Skills', name: 'skills' },
    { label: 'Position', name: 'position' },
    { label: 'Email', name: 'contact email' },
    { label: 'Other email', name: 'contact other email' },
    { label: 'Phone', name: 'contact phone' },
    { label: 'Mobile', name: 'contact mobile' },
    { label: 'Notes', name: 'notes' },
    { label: 'Date created', name: 'date created' },
    { label: 'Date updated', name: 'date updated' },
    { label: 'Unsubscribed', condId: 27 },
  ];
  const contactFieldsItems = liveFirst(contactFieldDefs.map(def => {
    if (def.forceSoon) return { label: def.label, status: 'soon', data: null };
    const c = def.condId ? findCondById(def.condId) : findCond(def.name);
    return { label: def.label, status: c ? 'live' : 'soon', data: c };
  }));

  const companyFieldsItems = [
    { label: 'Company name', status: findCond('company') ? 'live' : 'soon', data: findCond('company') },
    { label: 'City', status: findCond('city') ? 'live' : 'soon', data: findCond('city') },
    { label: 'State', status: findCond('state') ? 'live' : 'soon', data: findCond('state') },
    { label: 'Postcode', status: findCond('postal code') ? 'live' : 'soon', data: findCond('postal code') },
    { label: 'Country', status: findCond('country') ? 'live' : 'soon', data: findCond('country') },
    { label: 'Status is', status: 'soon', data: null },
    { label: 'Status changed to', status: 'soon', data: null },
    { label: 'Skill is', status: 'soon', data: null },
    { label: 'Created date', status: 'soon', data: null },
    { label: 'Updated date', status: 'soon', data: null },
    { label: 'Custom field', status: 'soon', data: null },
    { label: 'Parent', status: 'request', data: null },
    { label: 'In folder', status: 'request', data: null },
  ];

  const oppStageCond = findCond('opportunity stage');
  const opportunitiesItems = [
    { label: 'Opportunity stage is', status: oppStageCond ? 'live' : 'soon', data: oppStageCond },
  ];

  if (triggerType === TriggerTypeClient.ClientBased) {
    const aggregateJobStatusCond = findCondById(AutomationIfElseFirstColumnTypeClient.ClientAggregateJobStatus);
    const aggregatePlacementStatusCond = findCondById(AutomationIfElseFirstColumnTypeClient.ClientAggregatePlacementStatus);
    return {
      groups: [
        { name: 'Engagement', items: engagementItems },
        { name: 'Website', items: clientWebsiteItems },
        { name: 'Surveys / Forms', items: surveysFormsItems },
        { name: 'Contact fields', items: contactFieldsItems },
        { name: 'Company fields', items: liveFirst(companyFieldsItems) },
        { name: 'Opportunities', items: opportunitiesItems },
        { name: 'Jobs', items: [{ label: 'Job status', status: aggregateJobStatusCond ? 'live' : 'soon', data: aggregateJobStatusCond }] },
        { name: 'Placements', items: [{ label: 'Placement status', status: aggregatePlacementStatusCond ? 'live' : 'soon', data: aggregatePlacementStatusCond }] },
        { name: 'Activities', items: activitiesItems },
        ...(customFieldItems.length > 0 ? [{ name: 'Contact Custom Fields', items: customFieldItems }] : []),
      ],
      getSelectedLabel: makeConditionLabelGetter(1),
    };
  }

  if (triggerType === TriggerTypeClient.JobBased) {
    // ROI-358: Job status and Interview date resolve by id to the per-job conditions. Matching
    // "job status" by name found the contact aggregate condition (id 46, all of the contact's
    // jobs), which belongs to Contact campaigns only.
    const jobFieldDefs = [
      { label: 'Job status', condId: AutomationIfElseFirstColumnTypeClient.ClientJobStatus },
      { label: 'Application status', name: 'application status' },
      { label: 'Job work type', name: 'job work type' },
      { label: 'Job category', name: 'job category' },
      { label: 'Job type', name: 'job type' },
      { label: 'Job IDs', name: 'job ids' },
      { label: 'Job location', name: 'job location' },
      { label: 'Interview date', condId: AutomationIfElseFirstColumnTypeClient.ClientJobInterviewDate },
      { label: 'Job Date created', name: 'job date created' },
      { label: 'Job Date updated', name: 'job date updated' },
    ];
    const jobFieldItems = jobFieldDefs.map(def => {
      const c = def.condId ? findCondById(def.condId) : findCond(def.name);
      return { label: def.label, status: c ? 'live' : 'soon', data: c };
    });
    return {
      groups: [
        { name: 'Engagement', items: engagementItems },
        { name: 'Website', items: clientWebsiteItems },
        { name: 'Surveys / Forms', items: liveFirst(surveysFormsItems) },
        { name: 'Job fields', items: liveFirst(jobFieldItems) },
        { name: 'Contact fields', items: contactFieldsItems },
        { name: 'Company fields', items: liveFirst(companyFieldsItems) },
        { name: 'Activities', items: activitiesItems },
        ...(customFieldItems.length > 0 ? [{ name: 'Contact Custom Fields', items: customFieldItems }] : []),
        ...(jobCustomFieldItems.length > 0 ? [{ name: 'Job Custom Fields', items: jobCustomFieldItems }] : []),
      ],
      getSelectedLabel: makeConditionLabelGetter(1),
    };
  }

  if (triggerType === TriggerTypeClient.PlacementBased) {
    const placementFieldDefs = [
      { label: 'Placement status', name: 'placement status' },
      { label: 'Placement type', name: 'placement type' },
      { label: 'Start date', name: 'start date' },
      { label: 'End date', name: 'end date' },
      { label: 'Date created', name: 'date created' },
    ];
    const placementFieldItems = placementFieldDefs.map(def => {
      const c = findCond(def.name);
      return { label: def.label, status: c ? 'live' : 'soon', data: c };
    });
    placementFieldItems.push({ label: 'Payment type', status: 'request', data: null });
    placementFieldItems.push({ label: 'Salary / rates', status: 'request', data: null });
    placementFieldItems.push({ label: 'Work shift', status: 'request', data: null });
    placementFieldItems.push({ label: 'Placement source', status: 'request', data: null });
    return {
      groups: [
        { name: 'Engagement', items: engagementItems },
        { name: 'Website', items: clientWebsiteItems },
        { name: 'Surveys / Forms', items: liveFirst(surveysFormsItems) },
        { name: 'Placement fields', items: liveFirst(placementFieldItems) },
        { name: 'Contact fields', items: contactFieldsItems },
        { name: 'Company fields', items: liveFirst(companyFieldsItems) },
        { name: 'Activities', items: activitiesItems },
        ...(customFieldItems.length > 0 ? [{ name: 'Contact Custom Fields', items: customFieldItems }] : []),
        ...(placementCustomFieldItems.length > 0 ? [{ name: 'Placement Custom Fields', items: placementCustomFieldItems }] : []),
      ],
      getSelectedLabel: makeConditionLabelGetter(1),
    };
  }

  // Fallback
  return {
    groups: [
      { name: 'Engagement', items: engagementItems },
      { name: 'Website', items: clientWebsiteItems },
      { name: 'Surveys / Forms', items: surveysFormsItems },
      { name: 'Contact fields', items: contactFieldsItems },
      { name: 'Company fields', items: companyFieldsItems },
      { name: 'Opportunities', items: opportunitiesItems },
      { name: 'Activities', items: activitiesItems },
      ...(customFieldItems.length > 0 ? [{ name: 'Contact Custom Fields', items: customFieldItems }] : []),
    ],
    getSelectedLabel: makeConditionLabelGetter(1),
  };
}

function buildCandidateConditionGroups(triggerType, ctx) {
  const { findCond, findCondById, engagementItems, websiteItems, surveysFormsItems, activitiesItems, customFieldItems, placementCustomFieldItems, jobCustomFieldItems, applicationCustomFieldItems } = ctx;

  const candidateFieldDefs = [
    { label: 'Status', name: 'status' },
    { label: 'Rating', name: 'rating' },
    { label: 'Skills', name: 'skills' },
    { label: 'Availability', condId: AutomationIfElseFirstColumnType.CandidateAvailability },
    { label: 'Current position', name: 'current position' },
    { label: 'Current employer', name: 'current employer' },
    { label: 'Email', name: 'email' },
    { label: 'Secondary email', name: 'secondary email' },
    { label: 'Phone', name: 'phone' },
    { label: 'Mobile', name: 'mobile' },
    { label: 'City', name: 'city' },
    { label: 'State', name: 'state' },
    { label: 'Postal code', name: 'postal code' },
    { label: 'Country', name: 'country' },
    { label: 'Birthday', name: 'birthday' },
    { label: 'Notes', name: 'notes' },
    { label: 'Job application date', condId: AutomationIfElseFirstColumnType.CandidateJobApplicationDate },
    { label: 'Resume date', condId: AutomationIfElseFirstColumnType.CandidateResumeDate },
    { label: 'Document expiry date', condId: AutomationIfElseFirstColumnType.CandidateDocumentExpiryDate },
    { label: 'Document type', name: 'document type' },
    { label: 'Date created', name: 'date created' },
    { label: 'Date updated', name: 'date updated' },
    { label: 'Unsubscribed', condId: 28 },
    { label: 'Source', condId: 29 },
    { label: 'Contact method', condId: AutomationIfElseFirstColumnType.CandidateContactMethod },
    { label: 'Seeking', condId: AutomationIfElseFirstColumnType.CandidateSeeking, hideIfMissing: true },
  ];
  const candidateFieldItemsRaw = candidateFieldDefs.flatMap(def => {
    const c = def.condId ? findCondById(def.condId) : findCond(def.name);
    // Not in this ATS (e.g. Seeking on Bullhorn): omit rather than show as coming soon
    if (!c && def.hideIfMissing) return [];
    return [{ label: def.label, status: c ? 'live' : 'soon', data: c }];
  });
  const candidateFieldItems = liveFirst(candidateFieldItemsRaw);

  if (triggerType === TriggerType.CandidateBased) {
    return {
      groups: [
        { name: 'Engagement', items: engagementItems },
        { name: 'Website', items: websiteItems },
        { name: 'Surveys / Forms', items: surveysFormsItems },
        { name: 'Candidate fields', items: candidateFieldItems },
        { name: 'Activities', items: activitiesItems },
        ...(customFieldItems.length > 0 ? [{ name: 'Candidate Custom Fields', items: customFieldItems }] : []),
      ],
      getSelectedLabel: makeConditionLabelGetter(1),
    };
  }

  if (triggerType === TriggerType.JobBased) {
    // ROI-358: Job status and Interview date resolve by id to the per-job conditions, as on
    // Contact Jobs, so another condition with the same name can never take the slot.
    const jobFieldDefs = [
      { label: 'Job status', condId: AutomationIfElseFirstColumnType.CandidateJobStatus },
      { label: 'Application status', name: 'application status' },
      { label: 'Job work type', name: 'job work type' },
      { label: 'Job category', name: 'job category' },
      { label: 'Job type', name: 'job type' },
      { label: 'Job IDs', name: 'job ids' },
      { label: 'Job location', name: 'job location' },
      { label: 'Interview date', condId: AutomationIfElseFirstColumnType.CandidateJobInterviewDate },
      { label: 'Job Date created', name: 'job date created' },
      { label: 'Job Date updated', name: 'job date updated' },
    ];
    const jobFieldItems = jobFieldDefs.map(def => {
      const c = def.condId ? findCondById(def.condId) : findCond(def.name);
      return { label: def.label, status: c ? 'live' : 'soon', data: c };
    });
    return {
      groups: [
        { name: 'Engagement', items: engagementItems },
        { name: 'Website', items: websiteItems },
        { name: 'Surveys / Forms', items: liveFirst(surveysFormsItems) },
        { name: 'Job fields', items: liveFirst(jobFieldItems) },
        { name: 'Candidate fields', items: candidateFieldItems },
        { name: 'Activities', items: activitiesItems },
        ...(customFieldItems.length > 0 ? [{ name: 'Candidate Custom Fields', items: customFieldItems }] : []),
        ...(jobCustomFieldItems.length > 0 ? [{ name: 'Job Custom Fields', items: jobCustomFieldItems }] : []),
        ...(applicationCustomFieldItems.length > 0 ? [{ name: 'Application Custom Fields', items: applicationCustomFieldItems }] : []),
      ],
      getSelectedLabel: makeConditionLabelGetter(1),
    };
  }

  if (triggerType === TriggerType.PlacementBased) {
    const placementFieldDefs = [
      { label: 'Placement status', name: 'placement status' },
      { label: 'Placement type', name: 'placement type' },
      { label: 'Start date', name: 'start date' },
      { label: 'End date', name: 'end date' },
      { label: 'Date created', name: 'date created' },
    ];
    const placementFieldItems = placementFieldDefs.map(def => {
      const c = findCond(def.name);
      return { label: def.label, status: c ? 'live' : 'soon', data: c };
    });
    placementFieldItems.push({ label: 'Payment type', status: 'request', data: null });
    placementFieldItems.push({ label: 'Salary / rates', status: 'request', data: null });
    placementFieldItems.push({ label: 'Work shift', status: 'request', data: null });
    placementFieldItems.push({ label: 'Placement source', status: 'request', data: null });
    return {
      groups: [
        { name: 'Engagement', items: engagementItems },
        { name: 'Website', items: websiteItems },
        { name: 'Surveys / Forms', items: liveFirst(surveysFormsItems) },
        { name: 'Placement fields', items: liveFirst(placementFieldItems) },
        { name: 'Candidate fields', items: candidateFieldItems },
        { name: 'Activities', items: activitiesItems },
        ...(customFieldItems.length > 0 ? [{ name: 'Candidate Custom Fields', items: customFieldItems }] : []),
        ...(placementCustomFieldItems.length > 0 ? [{ name: 'Placement Custom Fields', items: placementCustomFieldItems }] : []),
      ],
      getSelectedLabel: makeConditionLabelGetter(1),
    };
  }

  // Fallback
  return {
    groups: [
      { name: 'Engagement', items: engagementItems },
      { name: 'Website', items: websiteItems },
      { name: 'Surveys / Forms', items: surveysFormsItems },
      { name: 'Candidate fields', items: candidateFieldItems },
      { name: 'Activities', items: activitiesItems },
      ...(customFieldItems.length > 0 ? [{ name: 'Candidate Custom Fields', items: customFieldItems }] : []),
    ],
    getSelectedLabel: makeConditionLabelGetter(1),
  };
}

function makeConditionLabelGetter(activityConditionId) {
  return function getSelectedLabel(formConditionItem) {
    if (!formConditionItem || !formConditionItem.firstColumn) return null;
    const id = formConditionItem.firstColumn.id;
    const activities = formConditionItem.thirdColumn && formConditionItem.thirdColumn.activities;
    if (id === activityConditionId && Array.isArray(activities) && activities.length === 1 && ACTIVITY_LABEL_MAP[activities[0]]) {
      return ACTIVITY_LABEL_MAP[activities[0]];
    }
    return formConditionItem.firstColumn.name || null;
  };
}
